import { useState, useRef, useEffect } from "react";
import { Link, Form } from "@remix-run/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { ArrowRightOnRectangleIcon } from "@heroicons/react/24/outline";
import { colors, shadows, typography } from "~/theme";

/**
 * TopNav Component - Pine Park Health Design System
 * 
 * Header navigation matching the Figma design at node-id=1683-1438
 * Features:
 * - Forest green logo area (56px wide, matches sidebar)
 * - White background with subtle shadow
 * - Breadcrumb navigation
 * - Prototype mode badge
 * - User avatar with initials and dropdown with logout
 */

type Breadcrumb = {
  label: string;
  path: string;
  isLast: boolean;
};

type TopNavProps = {
  userName?: string;
  userInitials?: string;
  userPhotoUrl?: string;
  userEmail?: string;
  onUserMenuClick?: () => void;
  breadcrumbs?: Breadcrumb[];
  showPrototypeBadge?: boolean;
};

// Helper to get initials from a name
function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function TopNav({ 
  userName = "User", 
  userInitials,
  userPhotoUrl,
  userEmail,
  onUserMenuClick,
  breadcrumbs = [],
  showPrototypeBadge = false,
}: TopNavProps) {
  const initials = userInitials || getInitials(userName);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  const handleMenuClick = () => {
    if (onUserMenuClick) {
      onUserMenuClick();
    } else {
      setIsMenuOpen(!isMenuOpen);
    }
  };
  
  return (
    <header style={{
      backgroundColor: colors.white,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '56px',
      paddingRight: '16px',
      boxShadow: shadows.sm,
      position: 'relative',
      zIndex: 100,
    }}>
      {/* Left side: Logo + Breadcrumbs */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
      }}>
        {/* Logo Area - 56px to match sidebar width */}
        <div style={{
          backgroundColor: colors.brand[850],
          width: '56px',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          {/* Pine Park Health Logo */}
          <svg 
            width="28" 
            height="24" 
            viewBox="0 0 50 44" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_logo)">
              <path d="M6.92249 12.7175C9.47207 11.6619 11.7163 9.94029 13.4382 7.7193C15.1601 5.49834 16.3012 2.85319 16.7512 0.0397357C16.7512 0.0397357 16.7512 0.0397403 16.7512 0.238452C16.7512 0.437164 16.7512 0.238452 16.7512 0.556391C18.328 4.24632 20.8727 7.40033 24.0942 9.65738C25.0801 10.3054 26.1318 10.8386 27.2301 11.2471C27.6431 11.3656 28.0792 11.3656 28.4922 11.2471C30.4652 10.5238 32.2745 9.38759 33.8082 7.90873C36.0346 5.80738 37.8185 3.24999 39.0476 0.397418C39.0269 0.265816 39.0269 0.131602 39.0476 0C39.5036 2.8235 40.6608 5.47446 42.4047 7.69091C44.1486 9.90741 46.4189 11.6126 48.9911 12.6381C48.188 13.3535 46.1228 13.4329 42.7956 12.956C43.9264 16.6731 45.9385 20.0351 48.6469 22.7326C45.6746 22.96 42.7018 22.298 40.0802 20.825C39.889 20.825 40.0802 21.2224 40.0802 21.4211C40.0802 26.071 40.3479 30.6811 40.3479 35.331C40.3479 36.245 40.1184 36.563 39.3536 36.8412C38.9942 36.966 38.6089 36.9876 38.2387 36.9036C37.8684 36.8197 37.5269 36.6333 37.2501 36.3643C37.1286 36.1468 37.0627 35.9004 37.0589 35.649C37.0589 33.5028 37.0589 31.317 37.0589 29.1709C37.0589 27.0248 37.0589 23.9646 37.0589 21.2224C36.768 21.3088 36.5042 21.4733 36.294 21.6993C33.8467 23.6539 30.8101 24.64 27.7274 24.4813C24.9794 24.3697 22.3497 23.2907 20.2697 21.4211C20.2697 21.4211 20.0403 21.1032 19.849 21.0635C19.6578 21.0237 19.849 21.4211 19.849 21.5801V26.6274C19.849 27.3428 19.849 27.3428 20.5374 27.2632C22.7066 26.9452 24.908 26.9452 27.0771 27.2632C28.2128 27.4206 29.3131 27.7838 30.328 28.3363C31.2681 28.8448 32.0406 29.6336 32.5461 30.6016C33.0152 31.5998 33.1883 32.7203 33.0433 33.8208C32.998 34.1817 32.9342 34.5399 32.8521 34.8939C32.8053 35.0723 32.7412 35.2454 32.6608 35.4105C32.6466 35.2253 32.6466 35.0393 32.6608 34.8541C32.5476 33.9326 32.1598 33.071 31.5518 32.39C30.9475 31.7153 30.1363 31.2798 29.2571 31.158C27.576 30.8698 25.8654 30.8164 24.1706 30.9991C22.7174 30.9991 21.3023 30.9991 19.8873 31.317C19.1606 31.317 19.1989 31.317 19.1606 32.1516C19.1224 32.9862 19.1606 34.4964 19.1606 35.6887C19.217 35.8831 19.217 36.0904 19.1606 36.2848C19.0924 36.4859 18.9736 36.6643 18.8164 36.8014C18.6612 36.945 18.4789 37.0533 18.281 37.1194H17.6691C17.3552 37.0778 17.0471 36.9977 16.7512 36.881C16.5007 36.7787 16.2869 36.5982 16.1393 36.3643C16.0606 36.1192 16.0606 35.8542 16.1393 35.6092C16.1393 30.9196 16.1393 26.23 16.4071 21.5404C16.4071 21.5404 16.4071 21.3019 16.4071 21.2224V20.9045C13.7354 22.4426 10.698 23.1594 7.64916 22.9711C9.1096 21.6268 10.3953 20.0904 11.4736 18.4007C12.5274 16.7564 13.3154 14.9441 13.8065 13.0355C11.5363 13.6808 9.12856 13.5696 6.92249 12.7175ZM24.553 11.6842H24.1324C23.7833 11.6443 23.4301 11.694 23.1039 11.8293C22.7776 11.9646 22.4884 12.1811 22.2617 12.4599C22.035 12.7387 21.8777 13.0711 21.8038 13.4279C21.7299 13.7847 21.7415 14.155 21.8377 14.5059C21.9065 15.0235 22.0349 15.5305 22.2202 16.0162C23.405 18.7003 25.3193 20.9659 27.7274 22.5339C27.9951 22.7326 28.148 22.5339 28.3775 22.5339C30.4465 21.1327 32.164 19.239 33.3874 17.0097C33.838 16.1759 34.124 15.2573 34.2289 14.3073C34.3117 13.8199 34.2268 13.3179 33.989 12.8891C33.7512 12.4604 33.3758 12.1323 32.9286 11.9624C32.6223 11.8429 32.2994 11.7758 31.9724 11.7637C30.741 11.7261 29.5235 12.0424 28.4539 12.6778C28.3122 12.7544 28.1548 12.7944 27.9951 12.7944C27.8353 12.7944 27.6778 12.7544 27.5361 12.6778C26.5899 12.1171 25.5269 11.8035 24.4383 11.7637L24.553 11.6842Z" fill="#4D8C78"/>
              <path d="M49.6408 34.9733C50.1379 35.6091 50.6734 37.9937 47.1167 40.6961C45.1491 42.1755 42.8888 43.1802 40.5004 43.6371C37.66 44.2015 34.7346 44.1066 31.9338 43.3589C30.371 43.0048 29.0006 42.0364 28.1093 40.6564C27.8364 40.2237 27.654 39.7362 27.5739 39.2257C27.4586 38.7294 27.4586 38.2117 27.5739 37.7155C27.8899 36.5986 28.4383 35.5674 29.1802 34.6951C29.1802 35.3309 29.1802 35.8078 29.1802 36.3245C29.2029 37.3388 29.5556 38.3151 30.1806 39.0945C30.8056 39.8739 31.6662 40.4104 32.6221 40.6166C33.7105 40.9943 34.8421 41.2214 35.9876 41.2923C38.1435 41.5008 40.3176 41.2564 42.3807 40.5736C44.4438 39.8908 46.3535 38.7837 47.9963 37.318C49.9467 35.7284 48.9906 34.9733 49.6408 34.9733Z" fill="#4D8C78"/>
              <path d="M0 35.5694C2.06531 33.9204 4.34418 32.5825 6.76922 31.5952C8.56672 30.8401 10.5937 30.1644 12.2381 29.5684C12.4252 29.5098 12.6247 29.5098 12.8118 29.5684C13.0102 29.612 13.1946 29.7077 13.3472 29.8466C13.6447 30.0504 13.8932 30.3222 14.0739 30.6413C14.2631 30.9575 14.3929 31.3082 14.4563 31.6746C14.4563 32.2311 14.4563 32.3106 13.8826 32.3901C10.226 32.9448 6.61913 33.8088 3.09778 34.9733L1.22381 35.6887C0.803134 35.8874 0.344198 35.9669 0 35.5694Z" fill="#4D8C78"/>
            </g>
            <defs>
              <clipPath id="clip0_logo">
                <rect width="50" height="44" fill="white"/>
              </clipPath>
            </defs>
          </svg>
        </div>

        {/* Breadcrumbs */}
        {breadcrumbs.length > 0 && (
          <nav style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            marginLeft: '16px',
          }}>
            <Link
              to="/"
              style={{
                color: colors.gray[400],
                textDecoration: 'none',
                fontSize: typography.fontSize.sm,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontFamily: typography.fontFamily.sans,
              }}
            >
              <span>←</span>
              <span>Grove</span>
            </Link>
            {breadcrumbs.map((crumb) => (
              <span
                key={crumb.path}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <span style={{ color: colors.gray[300] }}>/</span>
                {crumb.isLast ? (
                  <span style={{ 
                    color: colors.gray[900], 
                    fontWeight: typography.fontWeight.medium,
                    fontSize: typography.fontSize.sm,
                    fontFamily: typography.fontFamily.sans,
                  }}>
                    {crumb.label}
                  </span>
                ) : (
                  <Link
                    to={crumb.path}
                    style={{
                      color: colors.gray[400],
                      textDecoration: 'none',
                      fontSize: typography.fontSize.sm,
                      fontFamily: typography.fontFamily.sans,
                    }}
                  >
                    {crumb.label}
                  </Link>
                )}
              </span>
            ))}
          </nav>
        )}
      </div>

      {/* Right side: Badge + User Menu */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        {/* Prototype Mode Badge */}
        {showPrototypeBadge && (
          <span
            style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: 'white',
              padding: '4px 10px',
              borderRadius: '9999px',
              fontSize: '11px',
              fontWeight: typography.fontWeight.semibold,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontFamily: typography.fontFamily.sans,
            }}
          >
            Prototype
          </span>
        )}

        {/* User Menu */}
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button 
            onClick={handleMenuClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            {/* Avatar */}
            {userPhotoUrl ? (
              <img 
                src={userPhotoUrl} 
                alt={userName}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <div style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                backgroundColor: colors.gray[500],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <span style={{
                  color: colors.white,
                  fontSize: '12px',
                  fontFamily: typography.fontFamily.sans,
                  fontWeight: typography.fontWeight.normal,
                  lineHeight: 1,
                }}>
                  {initials}
                </span>
              </div>
            )}

            {/* User Name */}
            <span style={{
              color: colors.gray[900],
              fontSize: typography.fontSize.sm,
              fontFamily: typography.fontFamily.sans,
              fontWeight: typography.fontWeight.semibold,
              lineHeight: 1.5,
            }}>
              {userName}
            </span>

            {/* Dropdown Chevron */}
            <ChevronDownIcon 
              style={{ 
                width: '16px', 
                height: '16px', 
                color: colors.gray[900],
                transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }} 
            />
          </button>
          
          {/* Dropdown Menu */}
          {isMenuOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '4px',
              backgroundColor: colors.white,
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              border: `1px solid ${colors.gray[200]}`,
              minWidth: '200px',
              overflow: 'hidden',
              zIndex: 1000,
            }}>
              {/* User Info */}
              <div style={{
                padding: '12px 16px',
                borderBottom: `1px solid ${colors.gray[200]}`,
              }}>
                <div style={{
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.semibold,
                  color: colors.gray[900],
                  fontFamily: typography.fontFamily.sans,
                }}>
                  {userName}
                </div>
                {userEmail && (
                  <div style={{
                    fontSize: typography.fontSize.xs,
                    color: colors.gray[500],
                    fontFamily: typography.fontFamily.sans,
                    marginTop: '2px',
                  }}>
                    {userEmail}
                  </div>
                )}
              </div>
              
              {/* Sign Out Button */}
              <Form method="post" action="/logout">
                <button
                  type="submit"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '12px 16px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: typography.fontSize.sm,
                    color: colors.gray[700],
                    fontFamily: typography.fontFamily.sans,
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.gray[50];
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <ArrowRightOnRectangleIcon style={{ width: '16px', height: '16px' }} />
                  Sign Out
                </button>
              </Form>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
